import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiFolder, FiPlus, FiCopy, FiDownload, FiEye, FiTrash2 } from "react-icons/fi";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import {
  useFetchEcommerceImagesMutation,
  useAddEcommerceImagesMutation,
} from "../../../features/tenant/ecommerceImages/ecommerceImagesApi";
import { useFetchEcommerceColorsMutation } from "../../../features/tenant/ecommerceProduct/ecommerceColorsApi";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";

const EcommerceImages = () => {
  const [folders, setFolders] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  
  // Form states
  const [folderName, setFolderName] = useState("");
  const [colorOptions, setColorOptions] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]);
  const [colorImages, setColorImages] = useState({});
  const [isUploadingColorImages, setIsUploadingColorImages] = useState({});

  // API Hooks
  const [fetchFolders, { isLoading: isFetching }] = useFetchEcommerceImagesMutation();
  const [addFolder, { isLoading: isUploading }] = useAddEcommerceImagesMutation();
  const [fetchColors] = useFetchEcommerceColorsMutation();
  const [uploadSingleFile] = useUploadSingleFileMutation();

  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenant_id = vendorData?.tenant_id;
  const token = sessionStorage.getItem("access_token");

  const loadFolders = () => {
    fetchFolders({
      slug: tenant_id,
      token,
    })
      .unwrap()
      .then((res) => {
        if (res.body) {
          setFolders(res.body || []);
        }
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to fetch image folders");
      });
  };

  useEffect(() => {
    if (tenant_id) {
      loadFolders();
      fetchColors({ slug: tenant_id, token })
        .unwrap()
        .then((res) => {
          setColorOptions((res?.body || []).filter((color) => color.status === "1"));
        })
        .catch(() => toast.error("Failed to fetch colors"));
    }
  }, [tenant_id]);

  const totalImageCount = Object.values(colorImages).reduce(
    (total, images) => total + images.length,
    0,
  );

  const getFolderImages = (folder) => {
    if (Array.isArray(folder?.image_links) && folder.image_links.length) {
      return folder.image_links;
    }

    let images = folder?.images;
    if (typeof images === "string") {
      try {
        images = JSON.parse(images);
      } catch {
        images = [];
      }
    }

    return Array.isArray(images) ? images.flatMap((entry) => entry.urls || []) : [];
  };

  const handleAddColor = () => {
    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }

    const color = colorOptions.find((item) => item.id === selectedColor.id);
    if (!color || selectedColors.some((item) => item.id === color.id)) {
      toast.error("Color already added");
      return;
    }

    setSelectedColors((previous) => [...previous, color]);
    setColorImages((previous) => ({ ...previous, [color.id]: [] }));
    setSelectedColor(null);
  };

  const handleRemoveColor = (colorId) => {
    setSelectedColors((previous) => previous.filter((color) => color.id !== colorId));
    setColorImages((previous) => {
      const next = { ...previous };
      delete next[colorId];
      return next;
    });
  };

  const handleColorImageChange = async (colorId, files) => {
    const remaining = 15 - totalImageCount;
    const fileArray = Array.from(files || []);
    if (fileArray.length > remaining) {
      toast.error(`You can select a maximum of 15 images (${remaining} remaining)`);
      return;
    }

    const validFiles = fileArray.filter((file) =>
      ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type),
    );
    if (validFiles.length !== fileArray.length) {
      toast.error("Only PNG, JPG/JPEG, and WebP images are allowed");
    }
    if (!validFiles.length) return;

    setIsUploadingColorImages((previous) => ({ ...previous, [colorId]: true }));
    try {
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append("image", file);
        try {
          const response = await uploadSingleFile({ token, body: formData }).unwrap();
          const fileLink = response?.body?.fileLink;
          if (fileLink) {
            setColorImages((previous) => ({
              ...previous,
              [colorId]: [...(previous[colorId] || []), fileLink],
            }));
          } else {
            toast.error(`Failed to upload ${file.name}`);
          }
        } catch {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    } finally {
      setIsUploadingColorImages((previous) => ({ ...previous, [colorId]: false }));
    }
  };

  const handleRemoveColorImage = (colorId, imageIndex) => {
    setColorImages((previous) => ({
      ...previous,
      [colorId]: (previous[colorId] || []).filter((_, index) => index !== imageIndex),
    }));
  };

  const handleCopyLinks = (folder) => {
    let imagesJson = folder?.images;

    if (Array.isArray(imagesJson)) {
      imagesJson = JSON.stringify(imagesJson);
    }

    if (typeof imagesJson !== "string" || !imagesJson.trim()) {
      const links = getFolderImages(folder);
      imagesJson = links.length ? JSON.stringify(links) : "";
    }

    if (!imagesJson) {
      toast.error("No links to copy");
      return;
    }
    navigator.clipboard.writeText(imagesJson)
      .then(() => {
        toast.success("Image JSON copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy links");
      });
  };

  const handleDownloadCSV = (folder) => {
    const links = getFolderImages(folder);
    if (links.length === 0) {
      toast.error("No images in this folder to export");
      return;
    }

    const csvRows = [
      ["Folder Name", "Image URL"].map(val => `"${val.replace(/"/g, '""')}"`).join(",")
    ];
    
    for (const link of links) {
      csvRows.push([folder.folder_name, link].map(val => `"${val.replace(/"/g, '""')}"`).join(","));
    }

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", `${folder.folder_name}_images.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("CSV downloaded successfully");
  };

  const handleUploadFolder = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) {
      toast.error("Please enter a folder name");
      return;
    }

    if (selectedColors.length === 0) {
      toast.error("Please select at least one color");
      return;
    }

    const colorsWithoutImages = selectedColors.filter(
      (color) => !colorImages[color.id] || colorImages[color.id].length === 0,
    );
    if (colorsWithoutImages.length > 0) {
      toast.error(`Please upload images for: ${colorsWithoutImages.map((color) => color.color_name).join(", ")}`);
      return;
    }

    // Check unique folder name in existing list
    const folderExists = folders.some(
      (f) => f.folder_name.toLowerCase() === folderName.trim().toLowerCase()
    );
    if (folderExists) {
      toast.error("A folder with this name already exists");
      return;
    }

    const formData = new FormData();
    formData.append("folder_name", folderName.trim());
    const images = selectedColors.map((color) => ({
      urls: colorImages[color.id] || [],
      color_id: color.id,
      color_name: color.color_name,
    }));
    formData.append("images", JSON.stringify(images));
    // Keep the legacy field populated for older API/database consumers.
    formData.append("image_link", JSON.stringify(images.flatMap((entry) => entry.urls)));

    toast.loading("Uploading images and creating folder...", { id: "uploading-folder" });

    try {
      const response = await addFolder({
        slug: tenant_id,
        token,
        formData,
      }).unwrap();

      toast.dismiss("uploading-folder");
      if (response.success) {
        toast.success("Folder uploaded successfully!");
        setShowAddModal(false);
        setFolderName("");
        setSelectedColor(null);
        setSelectedColors([]);
        setColorImages({});
        loadFolders();
      } else {
        toast.error(response.message || "Upload failed");
      }
    } catch (err) {
      toast.dismiss("uploading-folder");
      toast.error(err?.data?.message || "Failed to upload folder");
    }
  };

  const openGallery = (folder) => {
    setSelectedFolder(folder);
    setShowGalleryModal(true);
  };

  return (
    <div className="flex flex-col w-full bg-gray-100 min-h-screen">
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Ecommerce Images</h1>
            <LinksComponent />
          </div>
          <button
            onClick={() => {
              setFolderName("");
              setSelectedColor(null);
              setSelectedColors([]);
              setColorImages({});
              setShowAddModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-1.5 shadow-md hover:shadow-lg active:scale-95"
          >
            <FiPlus className="text-lg" /> Add Folder
          </button>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto w-full flex-grow">
        {isFetching ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : folders.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200 shadow-sm max-w-lg mx-auto mt-12">
            <FiFolder className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-800 mb-1">No Image Folders Found</h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Create your first image folder to upload and organize your e-commerce product catalog images in bulk.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md active:scale-95 inline-flex items-center gap-1.5"
            >
              <FiPlus className="text-lg" /> Create Folder
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {folders.map((folder) => (
              <div
                key={folder.id}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all flex flex-col p-5 group"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-blue-50 rounded-xl text-blue-600 group-hover:bg-blue-100 transition-all">
                    <FiFolder className="text-3xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-800 truncate mb-0.5" title={folder.folder_name}>
                      {folder.folder_name}
                    </h3>
                    <p className="text-xs text-gray-500 font-medium">
                      {getFolderImages(folder).length} {getFolderImages(folder).length === 1 ? "Image" : "Images"}
                    </p>
                  </div>
                </div>

                <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                  <button
                    onClick={() => openGallery(folder)}
                    className="flex-1 py-2 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all flex items-center justify-center gap-1.5 border border-gray-200"
                  >
                    <FiEye /> View
                  </button>
                  <button
                    onClick={() => handleCopyLinks(folder)}
                    className="flex-1 py-2 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all flex items-center justify-center gap-1.5"
                  >
                    <FiCopy /> Copy Links
                  </button>
                  <button
                    onClick={() => handleDownloadCSV(folder)}
                    className="py-2 px-3 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-all flex items-center justify-center gap-1.5"
                    title="Download CSV"
                  >
                    <FiDownload /> Export
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Folder Modal */}
      <ModalComponent show={showAddModal} setShow={setShowAddModal} size="md">
        <form onSubmit={handleUploadFolder} className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Create New Image Folder</h2>
          <p className="text-xs text-gray-500 mb-6 leading-relaxed">
            Specify a folder name, select colors, and upload up to 15 images across those colors.
          </p>

          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-2 tracking-wide">
                Folder Name
              </label>
              <input
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="e.g. Summer Collection 2026"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-sm bg-gray-50"
                disabled={isUploading}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-2 tracking-wide">Colors</label>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <SelectComponent
                    title="Select Color"
                    dataArr={colorOptions.filter((color) => !selectedColors.some((selected) => selected.id === color.id)).map((color) => ({ id: color.id, name: color.color_name }))}
                    selected={selectedColor}
                    setSelected={setSelectedColor}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddColor}
                  className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium mb-1"
                  disabled={isUploading}
                >
                  <FiPlus /> Add
                </button>
              </div>
            </div>

            {selectedColors.length > 0 && (
              <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                {selectedColors.map((color) => {
                  const images = colorImages[color.id] || [];
                  return (
                    <div key={color.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full border border-gray-200" style={{ backgroundColor: color.hex_code }} />
                          <span className="text-sm font-semibold text-gray-700">{color.color_name}</span>
                          <span className="text-xs text-gray-400">{images.length} image{images.length === 1 ? "" : "s"}</span>
                        </div>
                        <button type="button" onClick={() => handleRemoveColor(color.id)} className="text-gray-400 hover:text-red-500" title="Remove color" disabled={isUploading}>
                          <FiTrash2 />
                        </button>
                      </div>
                      <label className="flex items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg p-3 text-xs font-semibold text-gray-600 cursor-pointer">
                        <FiPlus /> Upload images for {color.color_name}
                        <input
                          type="file"
                          multiple
                          accept="image/png, image/jpeg, image/jpg, image/webp"
                          onChange={(e) => { handleColorImageChange(color.id, e.target.files); e.target.value = null; }}
                          className="hidden"
                          disabled={isUploading || isUploadingColorImages[color.id] || totalImageCount >= 15}
                        />
                      </label>
                      {images.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          {images.map((image, index) => (
                            <div key={`${image}-${index}`} className="relative group border border-gray-200 rounded-lg overflow-hidden h-20 bg-gray-100">
                              <img src={image} alt={`${color.color_name} ${index + 1}`} className="w-full h-full object-cover" />
                              <button type="button" onClick={() => handleRemoveColorImage(color.id, index)} className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all text-white rounded-lg" disabled={isUploading}>
                                <FiTrash2 className="text-lg" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-[10px] text-gray-400">Selected: {totalImageCount}/15 images</p>
                </div>
            )}
          </div>

          <div className="mt-8 flex justify-end space-x-3 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold transition-colors border border-gray-200"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all shadow-md active:scale-95 ${
                isUploading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Create & Upload"}
            </button>
          </div>
        </form>
      </ModalComponent>

      {/* Gallery Modal */}
      <ModalComponent show={showGalleryModal} setShow={setShowGalleryModal} size="xl">
        <div className="p-6 max-h-[85vh] flex flex-col">
          <h2 className="text-xl font-bold text-gray-800 mb-1">{selectedFolder?.folder_name} Gallery</h2>
          <p className="text-xs text-gray-500 mb-6 font-medium">
            Viewing {getFolderImages(selectedFolder).length} images in this folder
          </p>

          <div className="flex-grow overflow-y-auto custom-scrollbar pr-1">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {getFolderImages(selectedFolder).map((link, idx) => (
                <div
                  key={idx}
                  className="group relative border border-gray-200 rounded-xl overflow-hidden bg-gray-50 hover:shadow-md transition-all flex flex-col h-44"
                >
                  <img
                    src={link}
                    alt={`Gallery item ${idx}`}
                    className="w-full flex-grow object-cover h-32"
                  />
                  <div className="p-2 border-t border-gray-100 flex items-center justify-between bg-white">
                    <span className="text-[10px] text-gray-400 font-semibold truncate flex-1 pr-2">
                      Image {idx + 1}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(link);
                        toast.success("Image URL copied!");
                      }}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-all"
                      title="Copy URL"
                    >
                      <FiCopy className="text-xs" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end border-t border-gray-100 pt-4">
            <button
              onClick={() => setShowGalleryModal(false)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2 rounded-lg text-sm font-semibold transition-colors border border-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default EcommerceImages;
